import { DEV_MODE } from '@/config/dev-mode'

/**
 * 개발 환경에서 키워드 검색을 수행하는 함수.
 * @async
 * @function findKeywords
 * @param {Object} params - API 요청에 사용할 쿼리 매개변수 객체.
 * @param {string} params.lang - 언어 설정 (예: "KOR").
 * @param {string} params.birth - 생년월일 (예: "1992-07-08", YYYY-MM-DD 형식).
 * @param {string} params.userName - 사용자 이름 (예: "익명").
 * @param {number} params.bigluckValue - 대운 값 (정수).
 * @param {number} params.gender - 성별 (1: 남성, 2: 여성).
 * @param {string} params.unknownTime - 출생 시간 미상 여부 ("Y" 또는 "N").
 * @param {string} params.keywordCategory - 키워드 카테고리 (예: "사주분석").
 * @param {string} params.keyword - 검색할 키워드 목록 (쉼표로 구분된 문자열, 예: "격국이름,용신선정").
 * @returns {Promise<Object>} API 응답 데이터를 포함한 Promise 객체.
 * @throws {Error} API 호출 실패 시 에러 발생.
 * @example
 * const params = {
 *   lang: "KOR",
 *   birth: "1992-07-08",
 *   userName: "익명",
 *   bigluckValue: 8,
 *   gender: 1,
 *   unknownTime: "N",
 *   keywordCategory: "사주분석",
 *   keyword: "격국이름,용신선정"
 * };
 */
const findKeywordsAPI = async (params) => {
  const pathDev = `/func_find_keywords_ext`;
  const pathProd = ``;
  const path = DEV_MODE ? pathDev : pathProd;

  const result = await axios.get(path, { params });
  return result.data;
};

/**
 * 용어사전 API를 호출하여 특정 키워드에 대한 정보를 검색합니다.
 * @async
 * @function findTermsAPI
 * @param {Object} params - API 요청에 사용할 쿼리 매개변수 객체.
 * @param {string} [params.keywordCategory="용어사전"] - 검색할 키워드의 카테고리 (기본값: "용어사전").
 * @param {string} params.keyword - 찾고자 하는 키워드 (예: "갑목,갑자일주").
 * @returns {Promise<Object>} API 응답 데이터를 포함한 Promise 객체.
 * @throws {Error} API 호출 실패 시 에러 발생.
 * @example
 * const params = {
 *   keywordCategory: "용어사전",
 *   keyword: "갑목,갑자일주"
 * };
 */
const findTermsAPI = async (params) => {
  const pathDev = `/func_find_terms_ext`;
  const pathProd = `/func_find_terms_ext`;
  const path = DEV_MODE ? pathDev : pathProd;

  const result = await axios.get(path, { params });
  return result.data;
}

import axios from "axios";

/**
 * 운세 차트 데이터를 조회하는 API.
 * @async
 * @function luckChartsAPI
 * @param {Object} params - API 요청에 사용할 쿼리 매개변수 객체.
 * @param {string} params.lang - 언어 설정 (예: "KOR" 또는 "CHN").
 * @param {string} params.birth - 생년월일 (예: "1992-07-08", YYYY-MM-DD 형식).
 * @param {string} params.userName - 사용자 이름.
 * @param {number} params.bigluckValue - 대운수.
 * @param {number} params.gender - 성별 (0=여자, 1=남자).
 * @param {string} params.unknownTime - 출생 시간 미상 여부 ("Y" 또는 "N").
 * @param {string} params.userbaseCode - 사주 정보 코드 (예: "경술을유정미임신").
 * @param {string} params.xlOptionType - 필요한 운세 정보 (전체생애, 전체대운, 전체년운, 전체월운 등).
 * @param {string} params.xlValue - 선택한 운세 옵션값 (예: "202502").
 * @param {string} params.selectedChart - 운세 옵션값 (예: "갑자, 2025").
 * @returns {Promise<Object>} API 응답 데이터를 포함한 Promise 객체.
 * @throws {Error} API 호출 실패 시 에러 발생.
 */
const luckChartsAPI = async (params) => {
  const pathDev = `/func_luck_charts_ext`;
  const pathProd = ``;
  const path = DEV_MODE ? pathDev : pathProd;

  const result = await axios.get(path, { params });
  return result.data;
};

/**
 * 생애/년운/월운/일운 상세 운세 데이터를 조회하는 API.
 * @async
 * @function luckDetailsAPI
 * @param {Object} params - API 요청에 사용할 쿼리 매개변수 객체.
 * @param {string} params.lang - 언어 설정 (예: "KOR" 또는 "CHN").
 * @param {string} params.birth - 생년월일 (예: "1992-07-08").
 * @param {string} params.userName - 사용자 이름.
 * @param {number} params.bigluckValue - 대운수.
 * @param {number} params.gender - 성별 (0=여자, 1=남자).
 * @param {string} params.unknownTime - 출생 시간 미상 여부 ("Y" 또는 "N").
 * @param {string} params.userbaseCode - 사주 정보 코드.
 * @param {string} params.xlOptionType - 필요한 운세 정보 (생애단일연령, 년운단일날짜 등).
 * @param {string} params.xlValue - 운세 옵션값 (예: "20250210").
 * @param {string} params.selectedLuck - 분석할 운세 타입 (예: "재물운, 학습운").
 * @param {string} params.luckType - 생활운세 / 생활추천 / 생활운세차트
 * @returns {Promise<Object>} API 응답 데이터를 포함한 Promise 객체.
 * @throws {Error} API 호출 실패 시 에러 발생.
 */
const luckDetailsAPI = async (params) => {
  const pathDev = `/func_luck_details_ext`;
  const pathProd = ``;
  const path = DEV_MODE ? pathDev : pathProd;

  const result = await axios.get(path, { params });
  return result.data;
};

/**
 * 특정 날짜의 낭만성향을 분석하는 API.
 * @async
 * @function luckNcodeStatsAPI
 * @param {Object} params - API 요청에 사용할 쿼리 매개변수 객체.
 * @param {string} params.lang - 언어 설정.
 * @param {string} params.birth - 생년월일.
 * @param {string} params.userName - 사용자 이름.
 * @param {number} params.bigluckValue - 대운수.
 * @param {number} params.gender - 성별 (0=여자, 1=남자).
 * @param {string} params.unknownTime - 출생 시간 미상 여부.
 * @param {string} params.userbaseCode - 사주 정보 코드.
 * @param {string} params.xlOptionType - 필요한 운세 정보 유형.
 * @param {string} params.xlValue - 분석할 날짜 (예: "202502").
 * @returns {Promise<Object>} API 응답 데이터를 포함한 Promise 객체.
 */
const luckNcodeStatsAPI = async (params) => {
  const pathDev = `/func_luck_ncode_stats_ext`;
  const pathProd = ``;
  const path = DEV_MODE ? pathDev : pathProd;

  const result = await axios.get(path, { params });
  return result.data;
};

/**
 * 사주와 관련된 용어를 조회하는 API.
 * @async
 * @function luckTermDictsAPI
 * @param {Object} params - API 요청에 사용할 쿼리 매개변수 객체.
 * @param {string} params.lang - 언어 설정.
 * @param {string} params.birth - 생년월일.
 * @param {string} params.userName - 사용자 이름.
 * @param {number} params.bigluckValue - 대운수.
 * @param {number} params.gender - 성별 (0=여자, 1=남자).
 * @param {string} params.unknownTime - 출생 시간 미상 여부.
 * @param {string} params.userbaseCode - 사주 정보 코드.
 * @param {string} params.xlOptionType - 필요한 운세 정보 유형.
 * @param {string} params.xlValue - 분석할 날짜.
 * @returns {Promise<Object>} API 응답 데이터를 포함한 Promise 객체.
 */
const luckTermDictsAPI = async (params) => {
  const pathDev = `/func_luck_term_dicts_ext`;
  const pathProd = ``;
  const path = DEV_MODE ? pathDev : pathProd;

  const result = await axios.get(path, { params });
  return result.data;
};

/**
 * 사용자의 진로, 반려동물, 오행 제안을 조회하는 API.
 * @async
 * @function userSuggestAPI
 * @param {Object} params - API 요청에 사용할 쿼리 매개변수 객체.
 * @param {string} params.lang - 언어 설정.
 * @param {string} params.birth - 생년월일.
 * @param {string} params.userName - 사용자 이름.
 * @param {number} params.bigluckValue - 대운수.
 * @param {number} params.gender - 성별.
 * @param {string} params.unknownTime - 출생 시간 미상 여부.
 * @param {string} params.userbaseCode - 사주 정보 코드.
 * @param {string} params.suggestionFlag - 제안 유형 (예: "직업진로제안, 반려친구제안").
 * @returns {Promise<Object>} API 응답 데이터를 포함한 Promise 객체.
 */
const userSuggestAPI = async (params) => {
  const pathDev = `/func_user_suggest_ext`;
  const pathProd = ``;
  const path = DEV_MODE ? pathDev : pathProd;

  const result = await axios.get(path, { params });
  return result.data;
};

/**
 * 월별일운 및 일운단일날짜를 분석하는 API.
 * @async
 * @function lifeLuckAnalyDlAPI
 * @param {Object} params - API 요청에 사용할 쿼리 매개변수 객체.
 * @param {string} params.lang - 언어 설정 (예: "KOR", "CHN").
 * @param {string} params.birth - 생년월일 (예: "1992-07-08", YYYY-MM-DD 형식).
 * @param {string} params.userName - 사용자 이름.
 * @param {number} params.bigluckValue - 대운수.
 * @param {number} params.gender - 성별 (0=여자, 1=남자).
 * @param {string} params.unknownTime - 출생 시간 미상 여부 ("Y" 또는 "N").
 * @param {string} params.userbaseCode - 사주 정보 코드 (예: "경술을유정미임신").
 * @param {string} params.xlOptionType - 필요한 운세 정보 (월별일운, 일운단일날짜 중 하나).
 * @param {string} params.xlValue - 운세 옵션값 (YYYYMM 또는 YYYYMMDD).
 * @returns {Promise<Object>} API 응답 데이터를 포함한 Promise 객체.
 * @throws {Error} API 호출 실패 시 에러 발생.
 * @example
 * const params = {
 *   lang: "KOR",
 *   birth: "1992-07-08",
 *   userName: "익명",
 *   bigluckValue: 8,
 *   gender: 1,
 *   unknownTime: "N",
 *   userbaseCode: "경술을유정미임신",
 *   xlOptionType: "일운단일날짜",
 *   xlValue: "20250211"
 * };
 */
const lifeLuckAnalyDlAPI = async (params) => {
  const pathDev = `/life_luck_anal_dl_ext`;
  const pathProd = ``;
  const path = DEV_MODE ? pathDev : pathProd;

  const result = await axios.get(path, { params });
  return result.data;
};

/**
 * 전체생애 및 생애단일연령을 분석하는 API.
 * @async
 * @function lifeLuckAnalyAPI
 * @param {Object} params - API 요청에 사용할 쿼리 매개변수 객체.
 * @param {string} params.lang - 언어 설정 (예: "KOR", "CHN").
 * @param {string} params.birth - 생년월일 (예: "1992-07-08").
 * @param {string} params.userName - 사용자 이름.
 * @param {number} params.bigluckValue - 대운수.
 * @param {number} params.gender - 성별 (0=여자, 1=남자).
 * @param {string} params.unknownTime - 출생 시간 미상 여부 ("Y" 또는 "N").
 * @param {string} params.userbaseCode - 사주 정보 코드.
 * @param {string} params.xlOptionType - 필요한 운세 정보 (전체생애, 생애단일연령).
 * @param {string} params.xlValue - 운세 옵션값 (None 또는 초년/청년/중년/장년/노년).
 * @returns {Promise<Object>} API 응답 데이터를 포함한 Promise 객체.
 * @throws {Error} API 호출 실패 시 에러 발생.
 * @example
 * const params = {
 *   lang: "KOR",
 *   birth: "1992-07-08",
 *   userName: "익명",
 *   bigluckValue: 8,
 *   gender: 1,
 *   unknownTime: "N",
 *   userbaseCode: "경술을유정미임신",
 *   xlOptionType: "전체생애",
 *   xlValue: "None"
 * };
 */
const lifeLuckAnalyAPI = async (params) => {
  const pathDev = `/life_luck_anal_ext`;
  const pathProd = ``;
  const path = DEV_MODE ? pathDev : pathProd;

  const result = await axios.get(path, { params });
  return result.data;
};

/**
 * 대운/년운/월운을 분석하는 API.
 * @async
 * @function lifeLuckAnalyXlAPI
 * @param {Object} params - API 요청에 사용할 쿼리 매개변수 객체.
 * @param {string} params.lang - 언어 설정 (예: "KOR", "CHN").
 * @param {string} params.birth - 생년월일 (예: "1992-07-08").
 * @param {string} params.userName - 사용자 이름.
 * @param {number} params.bigluckValue - 대운수.
 * @param {number} params.gender - 성별 (0=여자, 1=남자).
 * @param {string} params.unknownTime - 출생 시간 미상 여부 ("Y" 또는 "N").
 * @param {string} params.userbaseCode - 사주 정보 코드.
 * @param {string} params.xlOptionType - 필요한 운세 정보 (전체대운, 년도구간, 년운단일날짜, 전체월운, 월운단일날짜).
 * @param {string} params.xlValue - 운세 옵션값 (YYYY, YYYYMM, YYYYMMDD).
 * @returns {Promise<Object>} API 응답 데이터를 포함한 Promise 객체.
 * @throws {Error} API 호출 실패 시 에러 발생.
 * @example
 * const params = {
 *   lang: "KOR",
 *   birth: "1992-07-08",
 *   userName: "익명",
 *   bigluckValue: 8,
 *   gender: 1,
 *   unknownTime: "N",
 *   userbaseCode: "경술을유정미임신",
 *   xlOptionType: "년운단일날짜",
 *   xlValue: "2025"
 * };
 */
const lifeLuckAnalyXlAPI = async (params) => {
  const pathDev = `/life_luck_anal_xl_ext`;
  const pathProd = ``;
  const path = DEV_MODE ? pathDev : pathProd;

  const result = await axios.get(path, { params });
  return result.data;
};

/**
 * 낭만성향을 분석하는 API.
 * @async
 * @function lifeLuckNcodeAPI
 * @param {Object} params - API 요청에 사용할 쿼리 매개변수 객체.
 * @param {string} params.lang - 언어 설정 (예: "KOR", "CHN").
 * @param {string} params.birth - 생년월일 (예: "1992-07-08", YYYY-MM-DD 형식).
 * @param {string} params.userName - 사용자 이름.
 * @param {number} params.bigluckValue - 대운수.
 * @param {number} params.gender - 성별 (0=여자, 1=남자).
 * @param {string} params.unknownTime - 출생 시간 미상 여부 ("Y" 또는 "N").
 * @param {string} params.userbaseCode - 사주 정보 코드 (예: "경술을유정미임신").
 * @param {string} params.xlOptionType - 필요한 운세 정보 (생애단일연령, 년운단일날짜, 월운단일날짜, 일운단일날짜).
 * @param {string} params.xlValue - 운세 옵션값 (초년/청년/중년/장년/노년, YYYY, YYYYMM, YYYYMMDD).
 * @returns {Promise<Object>} API 응답 데이터를 포함한 Promise 객체.
 * @throws {Error} API 호출 실패 시 에러 발생.
 * @example
 * const params = {
 *   lang: "KOR",
 *   birth: "1992-07-08",
 *   userName: "익명",
 *   bigluckValue: 8,
 *   gender: 1,
 *   unknownTime: "N",
 *   userbaseCode: "경술을유정미임신",
 *   xlOptionType: "월운단일날짜",
 *   xlValue: "202502"
 * };
 */
const lifeLuckNcodeAPI = async (params) => {
  const pathDev = `/life_luck_ncode_ext`;
  const pathProd = ``;
  const path = DEV_MODE ? pathDev : pathProd;

  const result = await axios.get(path, { params });
  return result.data;
};

/**
 * 친구관계를 분석하는 API.
 * @async
 * @function relationAnalyAPI
 * @param {Object} params - API 요청에 사용할 쿼리 매개변수 객체.
 * @param {string} params.lang - 언어 설정 (예: "KOR", "CHN").
 * @param {string} params.birth - 생년월일 (예: "1992-07-08").
 * @param {string} params.userName - 사용자 이름.
 * @param {number} params.bigluckValue - 대운수.
 * @param {number} params.gender - 성별 (0=여자, 1=남자).
 * @param {string} params.unknownTime - 출생 시간 미상 여부 ("Y" 또는 "N").
 * @param {string} params.userbaseCode - 사주 정보 코드.
 * @param {string} params.relDDAtom - 궁합 일주 (예: "갑자,을축").
 * @param {string} params.relDDName - 궁합 이름 (예: "갑돌이,을동이").
 * @returns {Promise<Object>} API 응답 데이터를 포함한 Promise 객체.
 * @throws {Error} API 호출 실패 시 에러 발생.
 * @example
 * const params = {
 *   lang: "KOR",
 *   birth: "1992-07-08",
 *   userName: "익명",
 *   bigluckValue: 8,
 *   gender: 1,
 *   unknownTime: "N",
 *   userbaseCode: "경술을유정미임신",
 *   relDDAtom: "갑자,을축",
 *   relDDName: "갑돌이,을동이"
 * };
 */
const relationAnalyAPI = async (params) => {
  const pathDev = `/relation_anal_ext`;
  const pathProd = ``;
  const path = DEV_MODE ? pathDev : pathProd;

  const result = await axios.get(path, { params });
  return result.data;
};

/**
 * 사주명식을 분석하는 API.
 * @async
 * @function userCodeAnalyAPI
 * @param {Object} params - API 요청에 사용할 쿼리 매개변수 객체.
 * @param {string} params.lang - 언어 설정 (예: "KOR", "CHN").
 * @param {string} params.birth - 생년월일 (예: "1992-07-08").
 * @param {string} params.userName - 사용자 이름.
 * @param {number} params.bigluckValue - 대운수.
 * @param {number} params.gender - 성별 (0=여자, 1=남자).
 * @param {string} params.unknownTime - 출생 시간 미상 여부 ("Y" 또는 "N").
 * @param {string} params.userbaseCode - 사주 정보 코드.
 * @returns {Promise<Object>} API 응답 데이터를 포함한 Promise 객체.
 * @throws {Error} API 호출 실패 시 에러 발생.
 * @example
 * const params = {
 *   lang: "KOR",
 *   birth: "1992-07-08",
 *   userName: "익명",
 *   bigluckValue: 8,
 *   gender: 1,
 *   unknownTime: "N",
 *   userbaseCode: "경술을유정미임신"
 * };
 */
const userCodeAnalyAPI = async (params) => {
  const pathDev = `/user_code_anal_ext`;
  const pathProd = ``;
  const path = DEV_MODE ? pathDev : pathProd;

  const result = await axios.get(path, { params });
  return result.data;
};

export {
  findKeywordsAPI,
  findTermsAPI,
  luckChartsAPI,
  luckDetailsAPI,
  luckNcodeStatsAPI,
  userSuggestAPI,
  lifeLuckAnalyDlAPI,
  lifeLuckAnalyAPI,
  lifeLuckAnalyXlAPI,
  lifeLuckNcodeAPI,
  relationAnalyAPI,
  userCodeAnalyAPI,
}
